import {getLanguageCode} from './localization';
import {formatPlural, type PluralForms, selectPluralForm} from './plural';
import {interpolate} from './stringFormat';

export type TimeUnit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';

export interface JustNowConfig {
    justNowText?: string;
    justNowLimit?: number;
}

/** Mirrors the AEM time-measurements-config fragment: one plural-forms set per unit. */
export type TimeMeasurements = {
    [Unit in TimeUnit as `${Unit}PluralForms`]?: PluralForms | null;
};

export const TIME_UNITS: TimeUnit[] = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second'];

export const TIME_MULTIPLIERS: Record<Uppercase<TimeUnit>, number> = {
    YEAR: 365 * 24 * 60 * 60,
    MONTH: 30 * 24 * 60 * 60, // Approximate
    WEEK: 7 * 24 * 60 * 60,
    DAY: 24 * 60 * 60,
    HOUR: 60 * 60,
    MINUTE: 60,
    SECOND: 1,
};

const UNIT_SUFFIX = 'PluralForms';
const REMAINDER_DIGITS = 2;

interface ConfiguredUnit {
    forms: PluralForms;
    multiplier: number;
}

const getConfiguredUnits = (config: TimeMeasurements): ConfiguredUnit[] =>
    TIME_UNITS.flatMap((unit) => {
        const forms = config[`${unit}${UNIT_SUFFIX}` as keyof TimeMeasurements];

        if (!forms) return [];

        return [{forms, multiplier: TIME_MULTIPLIERS[unit.toUpperCase() as Uppercase<TimeUnit>]}];
    });

/** `{1}` is the leftover in the next smaller configured unit, so `{0}:{1} mins` renders 310 as `5:10 mins`. */
const getRemainder = (seconds: number, multiplier: number, smallerUnit?: ConfiguredUnit) => {
    if (!smallerUnit) return undefined;

    const remainder = Math.floor((seconds % multiplier) / smallerUnit.multiplier);

    return String(remainder).padStart(REMAINDER_DIGITS, '0');
};

/**
 * Formats a duration into the largest configured unit that divides it without remainder, or - with
 * `floorToLargest` - into the largest unit that fits. Cascades to smaller units while a unit cannot
 * represent the value or its plural form is missing, and returns '' below the smallest configured unit.
 * With `dayPluralForms`/`weekPluralForms`:
 *   < 1 day → `dayPluralForms.zero` ("Last day")
 *   1 day   → `dayPluralForms.one` ("Ends Tomorrow")
 *   7 days  → `weekPluralForms.one` ("1 week left")
 *   10 days → `dayPluralForms.other` ("10 days left")
 */
export const formatTime = (
    timeInSeconds: number,
    config: TimeMeasurements & JustNowConfig,
    locale: string = getLanguageCode(),
    floorToLargest = false
): string => {
    const configuredUnits = getConfiguredUnits(config);
    const smallestUnit = configuredUnits[configuredUnits.length - 1];

    if (!smallestUnit) return '';

    const {justNowText, justNowLimit} = config;

    if (justNowText && Number.isFinite(justNowLimit)) {
        const threshold =
            typeof justNowLimit === 'number' ? justNowLimit * smallestUnit.multiplier : smallestUnit.multiplier;

        if (timeInSeconds < threshold) return justNowText;
    }

    const roundedSeconds = Math.round(timeInSeconds);

    if (roundedSeconds <= 0) return formatPlural(0, smallestUnit.forms, locale) ?? '';

    for (const [index, {forms, multiplier}] of configuredUnits.entries()) {
        if (roundedSeconds < multiplier) continue;
        if (!floorToLargest && roundedSeconds % multiplier !== 0) continue;

        const count = floorToLargest ? Math.floor(roundedSeconds / multiplier) : roundedSeconds / multiplier;
        const template = selectPluralForm(count, forms, locale);

        if (!template) continue;

        return interpolate(template, count, getRemainder(roundedSeconds, multiplier, configuredUnits[index + 1]));
    }

    return '';
};
